import { TitleCasePipe } from '@angular/common';
import { rivetDelimiter } from '@rivethealth/data/delimiter';
import { ESSearchResponseBuckets } from '@rivethealth/data/elastic-search';
import { Node, Option, TreeOption } from '@rivethealth/data/options';
import { groupBy, sortBy } from 'lodash';

const titleCasePipe = new TitleCasePipe();

export function getTreeFromConcatenatedValues(values: string[]): TreeOption[] {
  const groups = groupBy(values, value => value.split(rivetDelimiter)[0]);
  const options: TreeOption[] = [];
  const titleCasePipe = new TitleCasePipe();
  for (const key of Object.keys(groups)) {
    options.push({
      id: key,
      title: titleCasePipe.transform(key),
      subtitle: null,
      children: sortBy(
        groups[key].map(child => ({
          id: child,
          title: titleCasePipe.transform(child.split(rivetDelimiter)[1]),
          subtitle: null,
          children: [],
        })),
        'title',
      ),
    });
  }
  return sortBy(options, 'title');
}

export function getTreeFromConcatenatedValuesBuckets(
  buckets: {
    [subAggregate: string]: any;
    doc_count: number;
    key: number | string;
    key_as_string?: string;
  }[],
  blankBucketTitle?: string,
): TreeOption[] {
  const groups = groupBy(
    buckets,
    bucket => bucket.key.toString().split(rivetDelimiter)[0],
  );
  const options: TreeOption[] = [];
  const titleCasePipe = new TitleCasePipe();
  for (const i of Object.keys(groups)) {
    options.push({
      id: i,
      title: titleCasePipe.transform(i) || blankBucketTitle || '',
      subtitle: null,
      children: sortBy(
        groups[i].map(child => ({
          id: child.key.toString(),
          title: titleCasePipe.transform(
            child.key.toString().split(rivetDelimiter)[1],
          ),
          subtitle: null,
          children: [],
          count: child.doc_count,
        })),
        'title',
      ),
      count: groups[i].reduce((red, acc) => red + acc.doc_count, 0),
    });
  }
  return sortBy(options, 'title');
}

export function getTooltipFromNode(
  node: Node | Option,
  displayId: boolean = false,
): string {
  const dash = ' - ';
  const id = displayId ? `${node.id}${dash}` : '';
  const title = node.title?.trim().length > 0 ? `${node.title}` : '';
  const extraDash = title && node.subtitle ? `${dash}` : '';
  const subtitle = node.subtitle ?? '';
  return `${id}${title}${extraDash}${subtitle}`;
}

//This return is compatible w/ both Option and TreeOption, but Typescript only realizes that if I make the return TreeOption
export function transformElasticSearchBucketsToOptions(
  buckets: ESSearchResponseBuckets | undefined,
  transformMode: 'titlecase' | 'uppercase' | 'split-titlecase',
): TreeOption[] {
  if (!buckets) {
    return [];
  }
  return sortBy(
    buckets.map(b => {
      if (transformMode == 'split-titlecase') {
        return {
          id: b.key.toString(),
          title: titleCasePipe.transform(
            b.key.toString().split(rivetDelimiter)[0],
          ),
          subtitle: titleCasePipe.transform(
            b.key.toString().split(rivetDelimiter)[1],
          ),
          children: [],
        };
      }
      const id =
        transformMode == 'uppercase'
          ? b.key.toString().toUpperCase()
          : b.key.toString();
      const title =
        transformMode == 'uppercase'
          ? b.key.toString().toUpperCase()
          : titleCasePipe.transform(b.key.toString());
      return {
        id,
        title,
        subtitle: null,
        children: [],
      };
    }) || [],
    'title',
  );
}

export function transformElasticSearchDoubleAggregationBucketsToOptions(
  buckets: ESSearchResponseBuckets | undefined,
  secondOrderAgg: string,
): TreeOption[] {
  if (!buckets) {
    return [];
  }
  const keyArray: string[] = [];
  buckets.forEach(bucket => {
    bucket[secondOrderAgg].buckets.forEach(subBucket => {
      keyArray.push(
        bucket.key.toString().concat(rivetDelimiter, subBucket.key.toString()),
      );
    });
  });
  return getTreeFromConcatenatedValues(keyArray);
}
